import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Lock, Check, Flame } from "lucide-react";
import { API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { StreakCard } from "@/components/athlete/StreakCard";
import { MissionCard } from "@/components/athlete/MissionCard";
import { CoachNoteCard } from "@/components/athlete/CoachNoteCard";

/**
 * JourneyPage — a dedicated destination for accountability + celebration.
 * Intentionally pulled OUT of the main dashboard so the dashboard feels
 * like a place to take action, not a place to be judged.
 *
 * Simple rule: every element here exists to make the athlete feel proud,
 * not pressured. No red callouts, no urgency, no shame.
 */
const JourneyPage = () => {
  const [journey, setJourney] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [j, e] = await Promise.all([
          fetch(`${API}/athlete/journey`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
          fetch(`${API}/athlete/engagement`, { credentials: "include" }).then((r) => r.ok ? r.json() : null),
        ]);
        setJourney(j);
        setEngagement(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const refreshEngagement = async () => {
    try {
      const r = await fetch(`${API}/athlete/engagement`, { credentials: "include" });
      if (r.ok) setEngagement(await r.json());
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-text-secondary">
          Loading your journey…
        </div>
      </div>
    );
  }

  if (!journey) return null;

  const earned = journey.badges.filter((b) => b.earned);
  const locked = journey.badges.filter((b) => !b.earned);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        {/* Back link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-white mb-6 transition-colors"
          data-testid="journey-back"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to dashboard
        </Link>

        {/* ===== RANK HERO ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-primary/20 via-surface to-surface border border-primary/30 rounded-2xl p-6 sm:p-8 text-center mb-6"
          data-testid="rank-hero"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-3">
            Your rank
          </p>
          <div className="text-6xl sm:text-7xl mb-3" aria-hidden>
            {journey.rank.emoji}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-white mb-2" data-testid="rank-label">
            {journey.rank.label}
          </h1>
          <p className="text-text-secondary text-sm mb-4" data-testid="journey-day-count">
            {journey.distinct_training_days} training day{journey.distinct_training_days === 1 ? "" : "s"} logged
          </p>

          {journey.next_rank && (
            <div className="max-w-sm mx-auto mt-4">
              <div className="flex items-center justify-between text-xs text-text-secondary mb-1.5">
                <span>{journey.rank.label}</span>
                <span>{journey.next_rank.label} {journey.next_rank.emoji}</span>
              </div>
              <div className="h-2 bg-[#1E293B] rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/70 rounded-full"
                  style={{
                    width: `${Math.min(100, 100 - (journey.next_rank.days_needed / (journey.next_rank.days_needed + journey.distinct_training_days)) * 100).toFixed(1)}%`,
                  }}
                />
              </div>
              <p className="text-xs text-text-muted mt-2" data-testid="next-rank-hint">
                {journey.next_rank.days_needed} more day{journey.next_rank.days_needed === 1 ? "" : "s"} to {journey.next_rank.label}
              </p>
            </div>
          )}
        </motion.div>

        {/* ===== CHAPTER NARRATIVE ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="bg-surface border border-border rounded-2xl p-5 sm:p-6 mb-6"
          data-testid="chapter-card"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold mb-2">
            {journey.chapter.title}
          </p>
          <p className="text-white text-base sm:text-lg leading-relaxed">
            {journey.chapter.body}
          </p>
        </motion.div>

        {/* Coach note + mission if active (move these here from dashboard) */}
        {engagement?.coach_note && (
          <div className="mb-6">
            <CoachNoteCard note={engagement.coach_note} />
          </div>
        )}
        {engagement?.mission && (
          <div className="mb-6">
            <MissionCard
              mission={engagement.mission}
              onStart={() => { window.location.href = "/workouts"; }}
              onResolved={refreshEngagement}
            />
          </div>
        )}

        {/* ===== STREAK + HEAT MAP ===== */}
        {engagement && (
          <div className="mb-6">
            <StreakCard engagement={engagement} />
          </div>
        )}

        {/* ===== BADGES ===== */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-surface border border-border rounded-2xl p-5 sm:p-6"
          data-testid="badges-card"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-white">Badges</h2>
            <span className="text-xs text-text-muted">
              {earned.length} of {journey.badges.length} earned
            </span>
          </div>

          {earned.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">
                Earned
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                {earned.map((b) => (
                  <BadgeTile key={b.key} badge={b} earned />
                ))}
              </div>
            </>
          )}

          {locked.length > 0 && (
            <>
              <p className="text-[10px] uppercase tracking-widest text-text-muted font-bold mb-2">
                Coming up
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {locked.map((b) => (
                  <BadgeTile key={b.key} badge={b} />
                ))}
              </div>
            </>
          )}
        </motion.div>

        {/* T-shirt teaser — only when approaching Insider */}
        {journey.rank.key === "regular" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="mt-6 bg-gradient-to-br from-amber-500/10 via-surface to-surface border border-amber-500/30 rounded-2xl p-5 sm:p-6 text-center"
            data-testid="tshirt-teaser"
          >
            <p className="text-4xl mb-2" aria-hidden>👕</p>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold mb-1">
              Paladin Insider Shirt — Shipping soon
            </p>
            <p className="text-white text-sm">
              Hit 30 training days to earn yours. Something real, in your hands, that can't be faked.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// ====================================================================
const BadgeTile = ({ badge, earned }) => (
  <div
    className={`relative rounded-xl p-3 text-center border transition-all ${
      earned
        ? "bg-emerald-500/10 border-emerald-500/30"
        : "bg-[#0B0F1A] border-border opacity-70"
    }`}
    data-testid={`badge-${badge.key}`}
  >
    <div className={`text-3xl mb-1 ${earned ? "" : "grayscale opacity-60"}`} aria-hidden>
      {badge.emoji}
    </div>
    <p className="text-xs font-bold text-white leading-tight mb-0.5">{badge.label}</p>
    <p className="text-[10px] text-text-muted leading-snug mb-1.5">{badge.desc}</p>
    {earned ? (
      <div className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-emerald-400 font-bold">
        <Check className="w-3 h-3" />
        Earned
      </div>
    ) : (
      <>
        <div className="h-1 bg-[#1E293B] rounded-full overflow-hidden mb-0.5">
          <div
            className="h-full bg-gradient-to-r from-primary/70 to-primary/40 rounded-full"
            style={{ width: `${(badge.progress * 100).toFixed(1)}%` }}
          />
        </div>
        <div className="inline-flex items-center gap-1 text-[9px] text-text-muted">
          <Lock className="w-2.5 h-2.5" />
          {Math.min(badge.value, badge.target)} / {badge.target}
        </div>
      </>
    )}
  </div>
);

export default JourneyPage;
